import EventEmitter from 'eventemitter3';

import { isServerSideRendering } from '../../../utils/isServerSideRendering';
import { CrossTabBroadcastMessageSchema } from '../crossTabBroadcast.schema';
import type { CrossTabBroadcast } from '../crossTabBroadcast.types';

type CreateCrossTabBroadcastParams = {
  channelName: string;
};

/**
 * Creates a cross-tab broadcast service instance using the BroadcastChannel API.
 *
 * Enables communication between multiple tabs/windows of the same application.
 * Messages are validated against the schema and invalid messages are silently ignored.
 *
 * In SSR or when BroadcastChannel is unavailable, returns a no-op implementation.
 *
 * @param params - Configuration parameters
 * @param params.channelName - The name of the broadcast channel to use
 * @returns A cross-tab broadcast service instance
 */
export const createCrossTabBroadcast = ({
  channelName,
}: CreateCrossTabBroadcastParams): CrossTabBroadcast => {
  // SSR check - return no-op implementation
  // eslint-disable-next-line no-restricted-globals
  if (isServerSideRendering() || typeof BroadcastChannel === 'undefined') {
    return {
      off: () => {},
      on: () => {},
      send: () => {},
    };
  }

  // eslint-disable-next-line no-restricted-globals
  const channel = new BroadcastChannel(channelName);
  const eventEmitter = new EventEmitter();

  // Message handler with validation
  const handleMessage = (messageEvent: MessageEvent) => {
    const parsed = CrossTabBroadcastMessageSchema.safeParse(messageEvent.data);

    if (!parsed.success) {
      // Silently ignore invalid messages (consistent with storage patterns)
      return;
    }

    const { args, event } = parsed.data;
    eventEmitter.emit(event, args);
  };

  channel.addEventListener('message', handleMessage);

  return {
    off: (event, callback) => {
      eventEmitter.off(event, callback);
    },

    on: (event, callback) => {
      eventEmitter.on(event, callback);
    },

    send: ({ args, event }) => {
      channel.postMessage({ args, event });
    },
  };
};
